'use client';

import { useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import ErrorBoundary from '@/components/ErrorBoundary';
import Image from 'next/image';

interface Supporter {
  id: string;
  username: string;
  tierName: string;
  tierBadge: string;
  tierColor: string;
  timestamp: string;
  steamAvatar?: string;
  discordUsername?: string;
}

function LiveSupporterFeedContent() {
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchRecentSupporters = async () => {
      try {
        const supabase = createClient();
        
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select(`
            id,
            created_at,
            user_id,
            tier:supporter_tiers(
              name,
              badge,
              color,
              tier_type
            ),
            profile:user_profiles!user_subscriptions_user_id_fkey(
              steam_username,
              steam_avatar,
              discord_username,
              full_name
            )
          `)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) {
          console.error('Error fetching supporters:', error);
          setError('Failed to load supporters');
          return;
        }

        if (data && Array.isArray(data)) {
          const formattedSupporters: Supporter[] = data.map((sub: any) => ({
            id: sub?.id || '',
            username: sub?.profile?.steam_username || sub?.profile?.discord_username || sub?.profile?.full_name || 'Anonymous',
            tierName: sub?.tier?.name || 'Supporter',
            tierBadge: sub?.tier?.badge || '🪨',
            tierColor: sub?.tier?.color || '#a3a380',
            timestamp: formatTimestamp(sub?.created_at || new Date().toISOString()),
            steamAvatar: sub?.profile?.steam_avatar,
            discordUsername: sub?.profile?.discord_username,
          }));
          
          // Duplicate for seamless infinite scroll
          setSupporters([...formattedSupporters, ...formattedSupporters]);
        }
      } catch (error) {
        console.error('Failed to fetch supporters:', error);
        setError('Failed to load supporters');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentSupporters();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchRecentSupporters, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (err) {
      console.error('Error formatting timestamp:', err);
      return 'Recently';
    }
  };

  return (
    <section className="py-20 px-6 relative overflow-hidden">
      {/* Background glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(106, 92, 255, 0.08) 0%, transparent 70%)'
        }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <div className="text-center mb-12 reveal-hidden animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-vanta-accent">
            RECENT SUPPORTERS
          </h2>
          <p className="text-vanta-muted text-lg max-w-2xl mx-auto">
            Join our growing community of supporters who help keep VANTA running
          </p>
        </div>

        {/* Scrolling Feed Container */}
        <div 
          className="relative overflow-hidden rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(26, 26, 36, 0.6) 0%, rgba(20, 20, 26, 0.8) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(106, 92, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-vanta-muted">Loading supporters...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-red-400">{error}</p>
            </div>
          ) : supporters.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-vanta-muted">Coming Soon</p>
            </div>
          ) : (
            <div 
              ref={scrollRef}
              className="flex gap-4 py-6 px-4"
              style={{
                animation: isPaused ? 'none' : 'scroll-left 40s linear infinite',
                width: 'max-content'
              }}
            >
              {supporters.map((supporter, index) => (
                <div
                  key={`${supporter.id}-${index}`}
                  className="flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 hover:scale-105 flex-shrink-0"
                  style={{
                    background: 'rgba(26, 26, 36, 0.8)',
                    border: `1px solid ${supporter?.tierColor || '#a3a380'}40`,
                    minWidth: '320px',
                    boxShadow: `0 4px 16px ${supporter?.tierColor || '#a3a380'}20`
                  }}
                >
                  {/* Avatar Section */}
                  <div className="flex gap-2 flex-shrink-0">
                    {/* Steam Avatar */}
                    {supporter?.steamAvatar && (
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${supporter?.tierColor || '#a3a380'}40, ${supporter?.tierColor || '#a3a380'}20)`,
                          border: `2px solid ${supporter?.tierColor || '#a3a380'}60`
                        }}
                      >
                        <Image
                          src={supporter.steamAvatar}
                          alt={`${supporter?.username || 'Supporter'} Steam`}
                          width={48}
                          height={48}
                          className="w-full h-full rounded-full object-cover"
                          loading="lazy"
                          quality={75}
                        />
                      </div>
                    )}
                    
                    {/* Fallback Badge if no Steam avatar */}
                    {!supporter?.steamAvatar && (
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${supporter?.tierColor || '#a3a380'}40, ${supporter?.tierColor || '#a3a380'}20)`,
                          border: `2px solid ${supporter?.tierColor || '#a3a380'}60`
                        }}
                      >
                        <span>{supporter?.tierBadge || '🪨'}</span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-white truncate text-sm">
                        {supporter?.username || 'Anonymous'}
                      </p>
                      <span 
                        className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{
                          background: `${supporter?.tierColor || '#a3a380'}30`,
                          color: supporter?.tierColor || '#a3a380',
                          border: `1px solid ${supporter?.tierColor || '#a3a380'}50`
                        }}
                      >
                        {supporter?.tierBadge || '🪨'} {supporter?.tierName || 'Supporter'}
                      </span>
                    </div>
                    <p className="text-xs text-vanta-muted">
                      {supporter?.timestamp || 'Recently'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Live indicator */}
        <div className="flex items-center justify-center gap-2 mt-6 text-sm text-vanta-muted">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>Live feed</span>
        </div>
      </div>

      <style jsx>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </section>
  );
}

export default function LiveSupporterFeed() {
  return (
    <ErrorBoundary componentName="Live Supporter Feed">
      <LiveSupporterFeedContent />
    </ErrorBoundary>
  );
}